import { useState } from "react";
import type { DeepRequired, FieldErrors, FieldErrorsImpl, FieldValues } from "react-hook-form";

interface IUseFormErrorHandler<T extends FieldValues> {
  values: T;
  errors?: Partial<FieldErrorsImpl<DeepRequired<T>>>;
  keyOrders?: string[];
  namingOrder?: "eastern" | "western";
  onToast: (message: string) => void;
}

export default function useFormErrorHandler<T extends FieldValues>({
  values,
  errors,
  keyOrders,
  namingOrder,
  onToast,
}: IUseFormErrorHandler<T>) {
  const [firstErrorKey, setFirstErrorKey] = useState<T[keyof T]>();

  /** 지정한 순서대로 훑어 첫 번째 에러 필드를 기록한다 */
  const onCommonError = (error: FieldErrors<T>): void => {
    const sortedKeys = keyOrders ? ["country"].concat(keyOrders) : Object.keys(values);

    // 서양식 이름 순서는 성/이름 위치를 뒤바꾼다
    if (namingOrder === "western") {
      const item = sortedKeys.splice(1, 1, sortedKeys[0]);
      sortedKeys.splice(0, 1, item[0]);
    }

    sortedKeys.some((key) => {
      if (error[key as keyof T]) {
        setFirstErrorKey(key as T[keyof T]);
        return true;
      }

      return false;
    });
  };

  /** 필드 표시 없이 에러 토스트만 띄운다 */
  const onCommonErrorMessage = (error: FieldErrors<T>): void => {
    const errorMessage = Object.values(error)[0]?.message;
    if (!errorMessage) return;

    onToast(errorMessage as string);
  };

  const displayError = (key: T[keyof T]) => {
    if (!errors || !Object.keys(errors).length) return;

    return {
      isError: firstErrorKey === key,
      errorMessage: firstErrorKey === key ? errors[key as keyof typeof errors]?.message : "",
    };
  };

  return { firstErrorKey, setFirstErrorKey, onCommonError, onCommonErrorMessage, displayError };
}
